import { Phone, ShoppingCart, Map, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const LOGO_URL = "/images/cave-city-pawn-logo.png";

const quickLinks = [
  { label: 'Firearms & Guns', href: '#categories' },
  { label: 'Electronics', href: '#categories' },
  { label: 'Musical Instruments', href: '#categories' },
  { label: 'Jewelry & Gold', href: '#categories' },
  { label: 'Power Tools', href: '#categories' },
  { label: 'Archery Equipment', href: '#gun-range' },
];

const serviceLinks = [
  { label: 'Pawn Loans', href: '#pawn-process' },
  { label: 'Sell Your Items', href: '#pawn-process' },
  { label: 'Free Appraisals', href: '#about' },
  { label: 'About Us', href: '#about' },
  { label: 'Contact Us', href: '#contact' },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-foreground text-bone-white pt-14 pb-10 md:pt-18 md:pb-12"
      role="contentinfo"
    >
      {/* Decorative stone ridge */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-dirt-brown via-stone-dark to-dirt-brown" />
      <div className="absolute top-1.5 left-0 right-0 h-px bg-stone-dark/30" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Cave City Pawn Shop & Trading Company"
              className="h-16 mb-4"
              width="auto"
              height={64}
            />
            <p className="text-sm text-stone-light font-body leading-relaxed mb-4">
              Your trusted pawn shop with over 35 years of experience. 
              Buy, sell, or pawn firearms, electronics, instruments, jewelry &amp; tools.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="tel:850-615-6105"
                data-testid="footer-phone-link"
                className="flex items-center gap-2 text-accent font-body font-bold text-lg hover:text-white transition-colors"
              >
                <Phone size={18} />
                850-615-6105
              </a>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <a
                href="https://www.facebook.com/CaveCityPawnShopandTradingCompany"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                className="w-10 h-10 bg-bone-white/10 border-2 border-bone-white/20 rounded-full flex items-center justify-center text-stone-light hover:bg-[#1877F2] hover:border-[#1877F2] hover:text-white transition-all duration-300"
                aria-label="Follow Cave City Pawn on Facebook"
              >
                <i className="fab fa-facebook-f text-base"></i>
              </a>
              <a
                href="https://www.youtube.com/channel/UCniqD6VRvkNH71GadmUJfKA"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube-link"
                className="w-10 h-10 bg-bone-white/10 border-2 border-bone-white/20 rounded-full flex items-center justify-center text-stone-light hover:bg-[#FF0000] hover:border-[#FF0000] hover:text-white transition-all duration-300"
                aria-label="Watch Cave City Pawn on YouTube"
              >
                <i className="fab fa-youtube text-base"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <nav aria-label="Product categories">
            <h4 className="font-heading text-base text-accent uppercase mb-4">What We Carry</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-stone-light font-body hover:text-accent transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Services */}
          <nav aria-label="Our services">
            <h4 className="font-heading text-base text-accent uppercase mb-4">Services</h4>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-stone-light font-body hover:text-accent transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Shop Online CTA */}
          <div>
            <h4 className="font-heading text-base text-accent uppercase mb-4">Shop Online</h4>
            <p className="text-sm text-stone-light font-body mb-4">
              Browse our full inventory online! Find great deals on firearms, electronics, and more.
            </p>
            <a
              href="https://alabamapawnandstorage.com/pawn-shop"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-shop-online-btn"
              className="cave-btn inline-flex items-center gap-2 px-5 py-3 bg-magma-red text-white font-heading text-sm uppercase rounded-rock shadow-soft hover:shadow-none transition-all"
            >
              <ShoppingCart size={16} />
              Shop Now
            </a>

            <div className="mt-6">
              <h4 className="font-heading text-base text-accent uppercase mb-2">Hours</h4>
              <p className="text-sm text-stone-light font-body">
                Mon-Fri: 10AM - 5PM<br />
                Saturday: 10AM - 2PM<br />
                Sunday: Closed
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-stone-dark/30">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-stone-dark font-body">
              &copy; {new Date().getFullYear()} Cave City Pawn Shop &amp; Trading Company. All rights reserved. Est. 2020.
            </p>
            <div className="flex items-center gap-3 text-xs font-body">
              <Link to="/locations" data-testid="footer-service-areas-link" className="text-stone-light hover:text-accent transition-colors flex items-center gap-1">
                <Map size={12} /> Service Areas
              </Link>
              <span className="text-stone-dark">|</span>
              <Link to="/sitemap" data-testid="footer-sitemap-link" className="text-stone-light hover:text-accent transition-colors flex items-center gap-1">
                <FileText size={12} /> Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
